import { useEffect, useState } from 'react';
import { downloadJournalCsv, fetchPersonJournal, saveJournalNote } from '@/lib/admin-data';
import {
  JOURNAL_KINDS,
  KIND_ICON,
  KIND_LABEL,
  countByKind,
  dataRows,
  groupByDay,
  timeOf,
  type JournalEntry,
  type JournalKind,
  type JournalPage,
} from '@/lib/person-journal';
import s from '@/pages/console/console.module.css';

/**
 * Person journal (Brian, 2026-09-17): everything one person did and everything
 * Elina said to them, in one column, newest first, so a pattern is visible and
 * Elina can be told how to answer this person. Moved here from the consumer
 * app's /admin on 2026-09-18; the rules did not change.
 *
 * Read only, and nothing here is computed. Every line is a row the server read
 * from a table the product already writes, and a value nobody measured renders
 * as "not measured" rather than as a zero.
 */
export function JournalDetail({ data }: { data: Record<string, unknown> }) {
  return (
    <dl className={s.jData}>
      {dataRows(data).map((r) => (
        <div key={r.key} className={s.jDataRow}>
          <dt>{r.label}</dt>
          <dd className={r.missing ? s.jMissing : undefined}>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function JournalRow({ e }: { e: JournalEntry }) {
  const [open, setOpen] = useState(false);
  const rows = dataRows(e.data);
  return (
    <div className={s.jEntry}>
      <span className={s.jIcon} aria-hidden="true">{KIND_ICON[e.kind] ?? '·'}</span>
      <div className={s.jBody}>
        <div className={s.jLine}>
          <span className={s.jTime}>{timeOf(e.at)}</span>
          <span className={s.jKind}>{KIND_LABEL[e.kind] ?? e.kind}</span>
          <span className={s.jSummary}>{e.summary}</span>
        </div>
        {rows.length > 0 && (
          <button type="button" className={s.linkBtn} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? 'Hide detail' : `Show detail (${rows.length})`}
          </button>
        )}
        {open && <JournalDetail data={e.data} />}
      </div>
    </div>
  );
}

export function JournalPanel({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [page, setPage] = useState<JournalPage | null>(null);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [kinds, setKinds] = useState<JournalKind[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [noteState, setNoteState] = useState<string | null>(null);
  const [csvState, setCsvState] = useState<string | null>(null);

  // A change of person or of filter starts the timeline again from the top.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setEntries([]);
    setPage(null);
    fetchPersonJournal({ userId, kinds: kinds.length ? kinds : null })
      .then((r) => { if (active) { setPage(r); setEntries(r.entries); } })
      .catch(() => { if (active) setError('Could not load this journal.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userId, kinds]);

  const loadMore = async () => {
    if (!page?.nextCursor) return;
    setLoading(true);
    try {
      const next = await fetchPersonJournal({ userId, cursor: page.nextCursor, kinds: kinds.length ? kinds : null });
      setPage(next);
      setEntries((prev) => [...prev, ...next.entries]);
    } catch {
      setError('Could not load the next page.');
    } finally {
      setLoading(false);
    }
  };

  const toggleKind = (k: JournalKind) => setKinds((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const addNote = async () => {
    if (!note.trim()) return;
    setNoteState(null);
    try {
      const saved = await saveJournalNote({ userId, body: note.trim() });
      setPage((p) => (p ? { ...p, notes: [saved, ...p.notes] } : p));
      setNote('');
      setNoteState('Saved.');
    } catch (err) {
      setNoteState(`Not saved: ${(err as Error).message}`);
    }
  };

  const downloadCsv = async () => {
    setCsvState('Preparing the file.');
    try {
      await downloadJournalCsv(userId);
      setCsvState('Downloaded.');
    } catch (err) {
      setCsvState(`Not downloaded: ${(err as Error).message}`);
    }
  };

  const counts = countByKind(entries);
  const days = groupByDay(entries);

  return (
    <aside className={s.panel} aria-label="Person journal">
      <div className={s.panelHead}>
        <div>
          <h2 className={s.panelTitle}>{page?.person ? (page.person.name || page.person.email) : 'Journal'}</h2>
          <div className={s.sub}>
            {page?.person
              ? `${page.person.email} · joined ${new Date(page.person.createdAt).toLocaleDateString()} · timezone ${page.person.timezone ?? 'not recorded'}`
              : 'Loading the person.'}
          </div>
        </div>
        <div className={s.panelActions}>
          <button type="button" className={s.btnGhost} onClick={downloadCsv}>Download CSV</button>
          <button type="button" className={s.linkBtn} onClick={onClose}>Close</button>
        </div>
      </div>
      {csvState && <div className={s.sub}>{csvState}</div>}

      {/* Notes sit at the top: this is the instruction, the timeline is the evidence. */}
      <div className={s.jNotes}>
        <strong>Notes for Elina</strong>
        <p className={s.sub} style={{ margin: 0 }}>How Elina should respond to this person. Only you and the team read this.</p>
        {page?.notes.map((nt) => (
          <div key={nt.id} className={s.jNote}>
            <div>{nt.body}</div>
            <div className={s.sub}>{nt.authorEmail ?? 'unknown author'} · {new Date(nt.createdAt).toLocaleString()}</div>
          </div>
        ))}
        {page && page.notes.length === 0 && <div className={s.sub}>Nothing written yet.</div>}
        <textarea className={s.draftBody} rows={3} value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="Keep her answers short with this one. She is in recovery season." />
        <div className={s.draftActions}>
          <button type="button" className={s.btn} disabled={!note.trim()} onClick={addNote}>Save note</button>
          {noteState && <span className={s.sub}>{noteState}</span>}
        </div>
      </div>

      <div className={s.filters} role="group" aria-label="Filter the journal by kind">
        <button type="button" className={`${s.filter} ${kinds.length === 0 ? s.filterOn : ''}`} onClick={() => setKinds([])}>
          Everything <span className={s.filterCount}>{entries.length}</span>
        </button>
        {JOURNAL_KINDS.map((k) => (
          <button key={k} type="button" aria-pressed={kinds.includes(k)} className={`${s.filter} ${kinds.includes(k) ? s.filterOn : ''}`} onClick={() => toggleKind(k)}>
            {KIND_ICON[k]} {KIND_LABEL[k]} <span className={s.filterCount}>{counts[k] ?? 0}</span>
          </button>
        ))}
      </div>

      {error && <div className={s.error}>{error}</div>}
      {page?.unavailable.map((u) => (
        <div key={u.kind} className={s.jWarn}>{KIND_LABEL[u.kind] ?? u.kind}: {u.reason}.</div>
      ))}

      {days.map((d) => (
        <div key={d.day} className={s.jDay}>
          <h3 className={s.jDayLabel}>{d.label}</h3>
          {d.entries.map((e) => <JournalRow key={e.id} e={e} />)}
        </div>
      ))}

      {!loading && entries.length === 0 && !error && <div className={s.empty}>Nothing recorded for this person yet.</div>}
      {loading && <div className={s.sub}>Loading</div>}
      {page?.nextCursor && (
        <button type="button" className={s.filter} disabled={loading} onClick={loadMore}>Load more</button>
      )}
    </aside>
  );
}
